import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowRightIcon, Button, CloseIcon, DeleteIcon, IaIcon, PlusIcon, Text } from '@viapeople/components'
import { MarkdownPreview } from '@viapeople/components/src/components/MarkdownEditor'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { useToast } from '../../utils/ToastContext'
import { queryClient, trpc } from '../../utils/trpc'

const getTrpcErrorCode = (error: unknown): string | undefined => {
	if (!error || typeof error !== 'object') return undefined
	const maybeError = error as { data?: { code?: string }; shape?: { data?: { code?: string } } }
	return maybeError.data?.code ?? maybeError.shape?.data?.code
}

interface OptimisticMessage {
	id: string
	role: 'user'
	content: string
	createdAt: Date
}

export function ChatWidget() {
	const { isEnabled, isLoading: isFlagLoading } = useFeatureFlag('ai-chatbot')
	const { showToast } = useToast()
	const [isOpen, setIsOpen] = useState(false)
	const [activeThreadId, setActiveThreadId] = useState<string | null>(null)
	const [draft, setDraft] = useState('')
	const [optimisticMessages, setOptimisticMessages] = useState<OptimisticMessage[]>([])
	const [isBlockedByModal, setIsBlockedByModal] = useState(false)
	const [hasActionBar, setHasActionBar] = useState(false)
	const messagesEndRef = useRef<HTMLDivElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	// Hide the widget while a non-fullscreen modal/dialog is open so the chat button
	// doesn't overlap centered confirmation/reject/decline dialogs. The fullscreen
	// modal used to render the cycle-execution form is intentionally ignored — the
	// chatbot must remain reachable while filling out a form. We also lift the
	// button above any ActionBar present on screen so it doesn't cover Submit/Cancel.
	useEffect(() => {
		const recompute = () => {
			const blockingDialog = document.querySelector(
				'[role="dialog"][aria-modal="true"][data-state="open"]:not([data-fullscreen="true"])',
			)
			setIsBlockedByModal(blockingDialog !== null)
			setHasActionBar(document.querySelector('[data-action-bar="true"]') !== null)
		}

		recompute()
		const observer = new MutationObserver(recompute)
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-state', 'data-fullscreen', 'data-action-bar'],
		})
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		if (isBlockedByModal) setIsOpen(false)
	}, [isBlockedByModal])

	const threadsQuery = useQuery({
		...trpc.client.chatbot.listThreads.queryOptions(),
		refetchOnWindowFocus: false,
		enabled: isOpen && isEnabled,
	})

	const createThreadMutation = useMutation({
		...trpc.client.chatbot.createThread.mutationOptions(),
		onSuccess: (thread) => {
			setActiveThreadId(thread.id)
			setOptimisticMessages([])
			void queryClient.invalidateQueries({ queryKey: trpc.client.chatbot.listThreads.queryKey() })
		},
		onError: () => {
			showToast('Failed to create conversation', 'error')
		},
	})

	const threadQuery = useQuery({
		...trpc.client.chatbot.getThread.queryOptions({
			threadId: activeThreadId ?? '',
		}),
		enabled: !!activeThreadId && isOpen,
		refetchOnWindowFocus: false,
	})

	const sendMessageMutation = useMutation({
		...trpc.client.chatbot.sendMessage.mutationOptions(),
		onSuccess: async (result) => {
			setDraft('')
			setOptimisticMessages([])
			if (result.threadId !== activeThreadId) {
				setActiveThreadId(result.threadId)
			}
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: trpc.client.chatbot.listThreads.queryKey() }),
				queryClient.invalidateQueries({
					queryKey: trpc.client.chatbot.getThread.queryKey({ threadId: result.threadId }),
				}),
			])
		},
		onError: (error) => {
			setOptimisticMessages([])
			const message =
				getTrpcErrorCode(error) === 'TOO_MANY_REQUESTS'
					? 'Too many chatbot requests. Please wait a moment and try again.'
					: 'Failed to send message. Please try again.'
			showToast(message, 'error')
		},
	})

	const deleteThreadMutation = useMutation({
		...trpc.client.chatbot.deleteThread.mutationOptions(),
		onSuccess: (_result, variables) => {
			if (activeThreadId === variables.threadId) {
				setActiveThreadId(null)
				setOptimisticMessages([])
			}
			void queryClient.invalidateQueries({ queryKey: trpc.client.chatbot.listThreads.queryKey() })
		},
		onError: () => {
			showToast('Failed to delete conversation', 'error')
		},
	})

	const serverMessages = useMemo(() => threadQuery.data?.messages || [], [threadQuery.data?.messages])

	const messages = useMemo(() => [...serverMessages, ...optimisticMessages], [serverMessages, optimisticMessages])

	useEffect(() => {
		if (isOpen && !activeThreadId && threadsQuery.data?.[0]?.id) {
			setActiveThreadId(threadsQuery.data[0].id)
		}
	}, [isOpen, activeThreadId, threadsQuery.data])

	const shouldFocus = isOpen && !!activeThreadId
	useEffect(() => {
		if (shouldFocus) {
			textareaRef.current?.focus()
		}
	}, [shouldFocus])

	const scrollToBottom = useCallback(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
	}, [])

	const prevMessageCountRef = useRef(0)
	useEffect(() => {
		const count = messages.length
		if (count > prevMessageCountRef.current) {
			scrollToBottom()
		}
		prevMessageCountRef.current = count
	}, [messages, scrollToBottom])

	const handleSend = useCallback(async () => {
		const message = draft.trim()
		if (!message || sendMessageMutation.isPending) return

		setOptimisticMessages((prev) => [
			...prev,
			{ id: `opt-${Date.now()}`, role: 'user', content: message, createdAt: new Date() },
		])
		setDraft('')

		await sendMessageMutation.mutateAsync({
			threadId: activeThreadId ?? undefined,
			message,
		})
	}, [draft, activeThreadId, sendMessageMutation])

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault()
				void handleSend()
			}
		},
		[handleSend],
	)

	const handleDeleteThread = useCallback(
		(threadId: string, e: React.MouseEvent) => {
			e.stopPropagation()
			deleteThreadMutation.mutate({ threadId })
		},
		[deleteThreadMutation],
	)

	if (isFlagLoading || !isEnabled || isBlockedByModal) {
		return null
	}

	return (
		<div className={`fixed right-5 z-[110] ${hasActionBar ? 'bottom-24' : 'bottom-5'}`}>
			{isOpen ? (
				<div className='w-[420px] h-[580px] rounded-lg border border-neutral-200 bg-neutral-0 shadow-lg flex flex-col overflow-hidden'>
					{/* Header */}
					<div className='flex items-center justify-between border-b border-neutral-200 px-4 py-3 bg-neutral-0'>
						<div className='flex items-center gap-2'>
							<IaIcon size={18} className='fill-primary-600' />
							<Text variant='small' weight='semibold'>
								Discover Assistant
							</Text>
						</div>
						<button
							type='button'
							onClick={() => setIsOpen(false)}
							className='inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 transition-colors'>
							<CloseIcon size={16} className='fill-current' />
						</button>
					</div>

					<div className='flex flex-1 min-h-0'>
						{/* Thread sidebar */}
						<div className='w-[140px] border-r border-neutral-200 bg-neutral-50 flex flex-col'>
							<div className='p-2'>
								<Button
									size='small'
									variant='primary'
									onClick={() => {
										setActiveThreadId(null)
										setOptimisticMessages([])
										setDraft('')
										createThreadMutation.mutate({})
									}}
									icon={PlusIcon}
									iconPosition='left'>
									New chat
								</Button>
							</div>
							<div className='flex-1 overflow-auto px-2 pb-2 space-y-0.5'>
								{threadsQuery.data?.map((thread) => (
									<div
										key={thread.id}
										className={`group flex items-center rounded-md cursor-pointer transition-colors ${
											activeThreadId === thread.id
												? 'bg-primary-50 text-primary-800'
												: 'text-neutral-700 hover:bg-neutral-100'
										}`}>
										<button
											type='button'
											onClick={() => {
												setActiveThreadId(thread.id)
												setOptimisticMessages([])
											}}
											className='flex-1 text-left px-2.5 py-2 min-w-0'>
											<Text variant='hint' className='truncate block'>
												{thread.title}
											</Text>
										</button>
										<button
											type='button'
											onClick={(e) => handleDeleteThread(thread.id, e)}
											className='hidden group-hover:flex shrink-0 h-6 w-6 items-center justify-center rounded text-neutral-400 hover:text-error-600 hover:bg-neutral-200 transition-colors mr-1 cursor-pointer'>
											<DeleteIcon size={12} className='fill-current' />
										</button>
									</div>
								))}
								{threadsQuery.data?.length === 0 && (
									<div className='px-2 py-4'>
										<Text variant='hint' className='text-neutral-400 text-center block'>
											No conversations yet
										</Text>
									</div>
								)}
							</div>
						</div>

						{/* Chat area */}
						<div className='flex-1 flex flex-col min-w-0'>
							{activeThreadId ? (
								<>
									{/* Thread title */}
									<div className='border-b border-neutral-100 px-4 py-2.5 bg-neutral-0'>
										<Text variant='hint' weight='semibold' className='truncate block text-neutral-600'>
											{threadQuery.data?.title || 'Conversation'}
										</Text>
									</div>

									{/* Messages */}
									<div className='flex-1 overflow-auto px-4 py-3 space-y-3'>
										{messages.map((message) => (
											<div
												key={message.id}
												className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
												{message.role === 'user' ? (
													<div className='max-w-[85%] rounded-lg rounded-br-sm bg-primary-600 text-neutral-0 px-3.5 py-2.5'>
														<Text variant='xsmall' className='whitespace-pre-wrap'>
															{message.content}
														</Text>
													</div>
												) : (
													<div className='max-w-[85%] rounded-lg rounded-bl-sm bg-neutral-100 text-neutral-800 px-3.5 py-2.5'>
														<MarkdownPreview
															markdown={message.content}
															className='text-sm leading-relaxed [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-sm [&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-1 [&_p]:mb-1.5 [&_ul]:mb-1.5 [&_ol]:mb-1.5 [&_li]:mb-0.5 [&>*:last-child]:mb-0'
														/>
													</div>
												)}
											</div>
										))}
										{sendMessageMutation.isPending && (
											<div className='flex justify-start'>
												<div className='bg-neutral-100 rounded-lg rounded-bl-sm px-3.5 py-2.5'>
													<div className='flex items-center gap-2'>
														<IaIcon size={14} className='animate-pulse fill-primary-600' />
														<Text variant='hint' className='text-neutral-500'>
															Thinking...
														</Text>
													</div>
												</div>
											</div>
										)}
										<div ref={messagesEndRef} />
									</div>
								</>
							) : (
								<div className='flex-1 flex flex-col items-center justify-center px-6 gap-3'>
									<IaIcon size={32} className='fill-neutral-300' />
									<Text variant='small' className='text-neutral-500 text-center'>
										Start a new conversation or select one from the sidebar
									</Text>
									<Button
										size='small'
										variant='secondary'
										onClick={() => createThreadMutation.mutate({})}
										icon={PlusIcon}
										iconPosition='left'>
										New chat
									</Button>
								</div>
							)}

							{/* Composer */}
							<div className='border-t border-neutral-200 p-3'>
								<div className='flex gap-2 items-end'>
									<textarea
										ref={textareaRef}
										value={draft}
										onChange={(e) => setDraft(e.target.value)}
										onKeyDown={handleKeyDown}
										placeholder='Ask something...'
										rows={1}
										maxLength={4000}
										className='flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-800 placeholder:text-neutral-400 resize-none focus:outline-none focus:border-primary-400 focus:border-[2px] transition-colors'
									/>
									<button
										type='button'
										onClick={() => void handleSend()}
										disabled={sendMessageMutation.isPending || !draft.trim()}
										className='h-9 w-9 rounded-md bg-primary-600 text-neutral-0 flex items-center justify-center hover:bg-primary-800 disabled:bg-primary-300 transition-colors cursor-pointer shrink-0'>
										<ArrowRightIcon size={16} className='fill-current' />
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<button
					type='button'
					aria-label='Open Discover Assistant'
					onClick={() => setIsOpen(true)}
					className='group h-14 w-14 rounded-full bg-primary-600 text-neutral-0 shadow-lg flex items-center justify-center hover:bg-primary-800 transition-colors cursor-pointer'>
					<IaIcon size={24} className='fill-neutral-0 group-hover:scale-110 transition-transform' />
				</button>
			)}
		</div>
	)
}
